<template>
  <div
    :class="[
      'cms-slider',
      {
        'is-vertical': vertical,
        'is-disabled': disabled,
        [`cms-slider--${size}`]: size
      }
    ]"
    :style="vertical ? { height } : undefined"
    role="slider"
    :aria-valuemin="min"
    :aria-valuemax="max"
    :aria-valuenow="modelValue"
    :aria-disabled="disabled"
  >
    <div class="cms-slider__runway" ref="sliderRunway" @click="onSliderClick">
      <div
        class="cms-slider__bar"
        :style="barStyle"
      ></div>

      <!-- 范围选择的两个滑块 -->
      <template v-if="range">
        <div
          v-for="(item, index) in [firstValue, secondValue]"
          :key="index"
          class="cms-slider__button-wrapper"
          :style="getButtonStyle(index)"
          @mousedown="onButtonDown($event, index)"
          @touchstart="onButtonDown($event, index)"
        >
          <div class="cms-slider__button" :class="{ 'hover': hovering === index }">
            <div v-if="showTooltip" class="cms-slider__tooltip" :class="{ 'show': dragging === index }">
              {{ formatValue(item) }}
            </div>
          </div>
        </div>
      </template>

      <!-- 单滑块 -->
      <template v-else>
        <div
          class="cms-slider__button-wrapper"
          :style="buttonStyle"
          @mousedown="onButtonDown"
          @touchstart="onButtonDown"
        >
          <div class="cms-slider__button" :class="{ 'hover': hovering }">
            <div v-if="showTooltip" class="cms-slider__tooltip" :class="{ 'show': dragging }">
              {{ formatValue(modelValue) }}
            </div>
          </div>
        </div>
      </template>

      <!-- 间断点 -->
      <template v-if="showStops">
        <div
          v-for="(item, index) in stops"
          :key="index"
          class="cms-slider__stop"
          :style="getStopStyle(item)"
        ></div>
      </template>
    </div>

    <!-- 输入框 -->
    <div v-if="showInput && !range" class="cms-slider__input">
      <input
        type="number"
        :min="min"
        :max="max"
        :step="step"
        :disabled="disabled"
        :value="modelValue"
        @input="onInput"
        @change="onInputChange"
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { sliderProps, sliderEmits } from './slider'

defineOptions({
  name: 'CmsSlider'
})

const props = defineProps(sliderProps)
const emit = defineEmits(sliderEmits)

// 滑块相关的状态
const dragging = ref(false)
const startX = ref(0)
const startY = ref(0)
const startPosition = ref(0)
const hovering = ref(false)
const sliderRunway = ref<HTMLElement>()
const currentValue = ref(props.modelValue)

// 计算滑块的位置
const buttonStyle = computed(() => {
  const position = `${((currentValue.value as number - props.min) / (props.max - props.min)) * 100}%`
  return props.vertical
    ? { bottom: position }
    : { left: position }
})

// 计算进度条的样式
const barStyle = computed(() => {
  const position = `${((currentValue.value as number - props.min) / (props.max - props.min)) * 100}%`
  return props.vertical
    ? { height: position, backgroundColor: props.color }
    : { width: position, backgroundColor: props.color }
})

// 格式化显示的值
const formatValue = (value: number) => {
  if (props.formatTooltip) {
    return props.formatTooltip(value)
  }
  return value
}

// 计算间断点位置
const stops = computed(() => {
  if (!props.showStops || props.min > props.max) return []
  if (props.step === 0) return []
  const stopCount = (props.max - props.min) / props.step
  const result = []
  for (let i = 1; i < stopCount; i++) {
    result.push(props.min + i * props.step)
  }
  return result
})

// 获取间断点的样式
const getStopStyle = (position: number) => {
  const percentage = `${((position - props.min) / (props.max - props.min)) * 100}%`
  return props.vertical
    ? { bottom: percentage }
    : { left: percentage }
}

// 处理滑块点击
const onButtonDown = (event: MouseEvent | TouchEvent) => {
  if (props.disabled) return
  event.preventDefault()
  dragging.value = true
  
  if (event instanceof MouseEvent) {
    startX.value = event.clientX
    startY.value = event.clientY
  } else {
    startX.value = event.touches[0].clientX
    startY.value = event.touches[0].clientY
  }
  
  startPosition.value = currentValue.value as number
  
  document.addEventListener('mousemove', onDragging)
  document.addEventListener('mouseup', onDragEnd)
  document.addEventListener('touchmove', onDragging)
  document.addEventListener('touchend', onDragEnd)
}

// 处理滑块拖动
const onDragging = (event: MouseEvent | TouchEvent) => {
  if (!dragging.value) return
  
  let diff
  if (event instanceof MouseEvent) {
    diff = props.vertical
      ? startY.value - event.clientY
      : event.clientX - startX.value
  } else {
    diff = props.vertical
      ? startY.value - event.touches[0].clientY
      : event.touches[0].clientX - startX.value
  }
  
  const sliderRect = sliderRunway.value?.getBoundingClientRect()
  const sliderLength = props.vertical ? sliderRect?.height : sliderRect?.width
  
  if (!sliderLength) return
  
  const percent = diff / sliderLength
  const newPosition = startPosition.value + percent * (props.max - props.min)
  
  updateValue(newPosition)
}

// 处理拖动结束
const onDragEnd = () => {
  if (dragging.value) {
    dragging.value = false
    emit('change', currentValue.value)
  }
  document.removeEventListener('mousemove', onDragging)
  document.removeEventListener('mouseup', onDragEnd)
  document.removeEventListener('touchmove', onDragging)
  document.removeEventListener('touchend', onDragEnd)
}

// 处理滑动条点击
const onSliderClick = (event: MouseEvent) => {
  if (props.disabled || dragging.value) return
  
  const sliderRect = sliderRunway.value?.getBoundingClientRect()
  if (!sliderRect) return
  
  const percent = props.vertical
    ? (sliderRect.bottom - event.clientY) / sliderRect.height
    : (event.clientX - sliderRect.left) / sliderRect.width
  
  const newPosition = props.min + percent * (props.max - props.min)
  updateValue(newPosition)
  emit('change', currentValue.value)
}

// 更新值
const updateValue = (value: number) => {
  let newValue = value
  
  // 处理步长
  if (props.step) {
    newValue = Math.round(newValue / props.step) * props.step
  }
  
  // 处理范围
  newValue = Math.max(props.min, Math.min(props.max, newValue))
  
  currentValue.value = newValue
  emit('update:modelValue', newValue)
  emit('input', newValue)
}

// 处理输入框输入
const onInput = (event: Event) => {
  const value = Number((event.target as HTMLInputElement).value)
  updateValue(value)
}

// 处理输入框变化
const onInputChange = (event: Event) => {
  const value = Number((event.target as HTMLInputElement).value)
  updateValue(value)
  emit('change', currentValue.value)
}

// 组件卸载时清理事件监听
onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onDragging)
  document.removeEventListener('mouseup', onDragEnd)
  document.removeEventListener('touchmove', onDragging)
  document.removeEventListener('touchend', onDragEnd)
})
</script>

<style>
.cms-slider {
  --cms-slider-main-bg-color: var(--cms-primary-color, #409eff);
  --cms-slider-runway-bg-color: #e4e7ed;
  --cms-slider-stop-bg-color: #909399;
  --cms-slider-disabled-color: #c0c4cc;

  display: flex;
  align-items: center;
  position: relative;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 32px;
  cursor: pointer;
}

.cms-slider.is-vertical {
  height: 100%;
  width: 32px;
  flex-direction: column;
}

.cms-slider.is-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.cms-slider__runway {
  flex: 1;
  position: relative;
  height: 6px;
  margin: 0 15px;
  background-color: var(--cms-slider-runway-bg-color);
  border-radius: 3px;
  cursor: pointer;
}

.cms-slider.is-vertical .cms-slider__runway {
  width: 6px;
  height: 100%;
  margin: 15px 0;
}

.cms-slider__bar {
  position: absolute;
  height: 6px;
  background-color: var(--cms-slider-main-bg-color);
  border-radius: 3px;
}

.cms-slider.is-vertical .cms-slider__bar {
  width: 6px;
}

.cms-slider__button-wrapper {
  position: absolute;
  top: -7px;
  width: 20px;
  height: 20px;
  text-align: center;
  line-height: 20px;
  z-index: 1;
  transform: translateX(-50%);
  background: transparent;
}

.cms-slider.is-vertical .cms-slider__button-wrapper {
  top: auto;
  left: -7px;
  transform: translateY(50%);
}

.cms-slider__button {
  width: 20px;
  height: 20px;
  border: 2px solid var(--cms-slider-main-bg-color);
  background-color: #fff;
  border-radius: 50%;
  transition: .2s;
  user-select: none;
  position: relative;
}

.cms-slider__button.hover,
.cms-slider__button:hover {
  transform: scale(1.1);
}

.cms-slider__button:active {
  transform: scale(1.2);
}

.cms-slider__stop {
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--cms-slider-stop-bg-color);
  transform: translateX(-50%);
}

.cms-slider.is-vertical .cms-slider__stop {
  transform: translateY(50%);
}

.cms-slider__tooltip {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.2;
  color: #fff;
  background-color: var(--cms-slider-main-bg-color);
  opacity: 0;
  transition: opacity .2s;
}

.cms-slider__tooltip.show {
  opacity: 1;
}

.cms-slider__tooltip::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 4px 4px 0 4px;
  border-color: var(--cms-slider-main-bg-color) transparent transparent transparent;
}

.cms-slider__input {
  margin-left: 15px;
  width: 80px;
}

.cms-slider__input input {
  width: 100%;
  height: 32px;
  padding: 0 8px;
  font-size: 14px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  transition: border-color .2s;
}

.cms-slider__input input:focus {
  outline: none;
  border-color: var(--cms-slider-main-bg-color);
}

.cms-slider--small {
  height: 24px;
}

.cms-slider--small .cms-slider__runway {
  height: 4px;
}

.cms-slider--small .cms-slider__bar {
  height: 4px;
}

.cms-slider--small .cms-slider__button {
  width: 16px;
  height: 16px;
}

.cms-slider--small .cms-slider__button-wrapper {
  width: 16px;
  height: 16px;
  top: -6px;
}

.cms-slider--large {
  height: 40px;
}

.cms-slider--large .cms-slider__runway {
  height: 8px;
}

.cms-slider--large .cms-slider__bar {
  height: 8px;
}

.cms-slider--large .cms-slider__button {
  width: 24px;
  height: 24px;
}

.cms-slider--large .cms-slider__button-wrapper {
  width: 24px;
  height: 24px;
  top: -8px;
}
</style>